import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { lookupBortle } from '@/utils/bortleLookup';

export interface TravelItinerary {
  id: string;
  user_id: string;
  name: string;
  notes: string | null;
  slug: string | null;
  is_public: boolean;
  view_count: number;
  created_at: string;
  updated_at: string;
}

export interface TravelStop {
  id: string;
  itinerary_id: string;
  location_name: string;
  latitude: number;
  longitude: number;
  arrival_date: string;
  departure_date: string;
  stop_order: number;
  notes: string | null;
  created_at: string;
}

export interface TravelItineraryWithStops extends TravelItinerary {
  stops: TravelStop[];
}

export const useTravelItineraries = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const queryKey = ['travel-itineraries', user?.id];

  const { data: itineraries = [], isLoading: loading, error: queryError } = useQuery({
    queryKey,
    queryFn: async () => {
      // Fetch itineraries
      const { data: itineraryData, error: itineraryError } = await supabase
        .from('travel_itineraries')
        .select('*')
        .eq('user_id', user!.id)
        .order('updated_at', { ascending: false });

      if (itineraryError) throw itineraryError;

      if (!itineraryData || itineraryData.length === 0) {
        return [] as TravelItineraryWithStops[];
      }

      // Fetch all stops for user's itineraries
      const itineraryIds = itineraryData.map(i => i.id);
      const { data: stopsData, error: stopsError } = await supabase
        .from('travel_stops')
        .select('*')
        .in('itinerary_id', itineraryIds)
        .order('stop_order', { ascending: true });

      if (stopsError) throw stopsError;

      // Combine itineraries with their stops
      return itineraryData.map(itinerary => ({
        ...itinerary,
        stops: (stopsData || []).filter(stop => stop.itinerary_id === itinerary.id)
      })) as TravelItineraryWithStops[];
    },
    enabled: !!user,
    staleTime: 30_000,
  });

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey });
  }, [queryClient, queryKey]);

  const error = queryError?.message || null;

  const createItinerary = async (name: string, notes?: string): Promise<TravelItinerary | null> => {
    if (!user) return null;

    try {
      const { data, error } = await supabase
        .from('travel_itineraries')
        .insert({
          user_id: user.id,
          name,
          notes: notes || null
        })
        .select()
        .single();

      if (error) throw error;

      invalidate();
      toast({
        title: 'Trip created',
        description: `"${name}" has been created successfully`
      });
      return data;
    } catch (err) {
      console.error('Error creating itinerary:', err);
      toast({
        title: 'Error',
        description: 'Failed to create trip',
        variant: 'destructive'
      });
      return null;
    }
  };

  const updateItinerary = async (id: string, updates: { name?: string; notes?: string }): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('travel_itineraries')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      invalidate();
      toast({
        title: 'Trip updated',
        description: 'Your changes have been saved'
      });
      return true;
    } catch (err) {
      console.error('Error updating itinerary:', err);
      toast({
        title: 'Error',
        description: 'Failed to update trip',
        variant: 'destructive'
      });
      return false;
    }
  };

  const deleteItinerary = async (id: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('travel_itineraries')
        .delete()
        .eq('id', id);

      if (error) throw error;

      invalidate();
      toast({
        title: 'Trip deleted',
        description: 'The trip has been removed'
      });
      return true;
    } catch (err) {
      console.error('Error deleting itinerary:', err);
      toast({
        title: 'Error',
        description: 'Failed to delete trip',
        variant: 'destructive'
      });
      return false;
    }
  };

  const addStop = async (
    itineraryId: string,
    stop: Omit<TravelStop, 'id' | 'itinerary_id' | 'created_at'>
  ): Promise<TravelStop | null> => {
    if (!user) return null;

    try {
      const { data, error } = await supabase
        .from('travel_stops')
        .insert({
          itinerary_id: itineraryId,
          ...stop
        })
        .select()
        .single();

      if (error) throw error;

      // Also save as favorite location if not already saved
      const { data: existingFavorite } = await supabase
        .from('favorite_locations')
        .select('id')
        .eq('user_id', user.id)
        .eq('latitude', stop.latitude)
        .eq('longitude', stop.longitude)
        .maybeSingle();

      if (!existingFavorite) {
        const bortle = await lookupBortle(stop.latitude, stop.longitude).catch(() => null);
        await supabase
          .from('favorite_locations')
          .insert({
            user_id: user.id,
            name: stop.location_name,
            display_name: stop.location_name,
            latitude: stop.latitude,
            longitude: stop.longitude,
            bortle_class: bortle,
          });
      }

      invalidate();
      return data;
    } catch (err) {
      console.error('Error adding stop:', err);
      toast({
        title: 'Error',
        description: 'Failed to add stop',
        variant: 'destructive'
      });
      return null;
    }
  };

  const updateStop = async (stopId: string, updates: Partial<Omit<TravelStop, 'id' | 'itinerary_id' | 'created_at'>>): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('travel_stops')
        .update(updates)
        .eq('id', stopId);

      if (error) throw error;

      invalidate();
      return true;
    } catch (err) {
      console.error('Error updating stop:', err);
      toast({
        title: 'Error',
        description: 'Failed to update stop',
        variant: 'destructive'
      });
      return false;
    }
  };

  const deleteStop = async (stopId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('travel_stops')
        .delete()
        .eq('id', stopId);

      if (error) throw error;

      invalidate();
      return true;
    } catch (err) {
      console.error('Error deleting stop:', err);
      toast({
        title: 'Error',
        description: 'Failed to delete stop',
        variant: 'destructive'
      });
      return false;
    }
  };

  const reorderStops = async (itineraryId: string, stopIds: string[]): Promise<boolean> => {
    try {
      const updates = stopIds.map((id, index) =>
        supabase
          .from('travel_stops')
          .update({ stop_order: index + 1 })
          .eq('id', id)
      );

      await Promise.all(updates);
      invalidate();
      return true;
    } catch (err) {
      console.error('Error reordering stops:', err);
      toast({
        title: 'Error',
        description: 'Failed to reorder stops',
        variant: 'destructive'
      });
      return false;
    }
  };

  const generateSlug = (name: string): string => {
    const base = name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .substring(0, 40);
    const rand = Math.random().toString(36).substring(2, 6);
    return `${base}-${rand}`;
  };

  const shareTrip = async (id: string): Promise<string | null> => {
    try {
      // Check if trip already has a slug
      const trip = itineraries.find(i => i.id === id);
      const slug = trip?.slug || generateSlug(trip?.name || 'trip');

      const { error } = await supabase
        .from('travel_itineraries')
        .update({ slug, is_public: true })
        .eq('id', id);

      if (error) throw error;

      invalidate();
      return slug;
    } catch (err) {
      console.error('Error sharing trip:', err);
      toast({
        title: 'Error',
        description: 'Failed to share trip',
        variant: 'destructive'
      });
      return null;
    }
  };

  const unshareTrip = async (id: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('travel_itineraries')
        .update({ is_public: false })
        .eq('id', id);

      if (error) throw error;

      invalidate();
      toast({
        title: 'Trip is now private',
        description: 'The shared link has been deactivated'
      });
      return true;
    } catch (err) {
      console.error('Error unsharing trip:', err);
      toast({
        title: 'Error',
        description: 'Failed to make trip private',
        variant: 'destructive'
      });
      return false;
    }
  };

  return {
    itineraries,
    loading,
    error,
    refetch: invalidate,
    createItinerary,
    updateItinerary,
    deleteItinerary,
    addStop,
    updateStop,
    deleteStop,
    reorderStops,
    shareTrip,
    unshareTrip
  };
};
